import React, { useId, useMemo } from 'react';
import { Card, CardContent, CardHeader, Typography, Box } from '@mui/material';

/** kPa → fill colour (low = light blue, high = deep blue / red tint) */
function colorForPressure(p, minP, maxP) {
  if (!Number.isFinite(p)) return '#eceff1';
  if (maxP <= minP + 1e-6) return '#e3f2fd';
  const t = Math.max(0, Math.min(1, (p - minP) / (maxP - minP)));
  const r = Math.round(227 + t * (25 - 227));
  const g = Math.round(242 + t * (118 - 242));
  const b = Math.round(253 + t * (210 - 253));
  return `rgb(${r},${g},${b})`;
}

/** True when a pressure grid is present and every cell is zero. */
export function capElementPressuresAllZero(distribution) {
  if (!Array.isArray(distribution) || distribution.length === 0) return false;
  return distribution.every((p) => Math.abs(Number(p?.pressure_kPa) || 0) < 1e-6);
}

const CapElementPressureMatrix = ({ results, title, subheader }) => {
  const { foundation_geometry, equilibrium, input_parameters } = results || {};
  const dist = equilibrium?.slab_bearing_pressure_distribution;
  const axisMarkerId = `capPressureAxisArrow-${useId().replace(/:/g, '')}`;
  const allZero = capElementPressuresAllZero(dist);

  const { svg, legend, nx, ny, minP, maxP } = useMemo(() => {
    if (!foundation_geometry?.dimensions || !Array.isArray(dist) || dist.length === 0) {
      return { svg: null, legend: null, nx: 0, ny: 0, minP: 0, maxP: 0 };
    }

    const L = foundation_geometry.dimensions.L || 2;
    const W = foundation_geometry.dimensions.W || 2;
    const scale = 56;
    const capW = W * scale;
    const capH = L * scale;
    /** Side margin + room past cap centre for +X axis label (so annotations are not clipped). */
    const padH = 48;
    const svgW = Math.max(capW + 2 * padH + 80, 520);
    const offsetX = (svgW - capW) / 2;
    const titleBlockH = 40;
    const offsetY = titleBlockH + 8;
    const contentBottom = offsetY + capH + 28;
    const svgH = Math.max(contentBottom + 32, 300);
    const translateY = Math.max(0, (svgH - contentBottom) / 2);

    const uniqueX = [...new Set(dist.map((p) => p.x))].sort((a, b) => a - b);
    const uniqueY = [...new Set(dist.map((p) => p.y))].sort((a, b) => a - b);
    const nxEff = uniqueX.length || 8;
    const nyEff = uniqueY.length || 8;
    const dx = L / nxEff;
    const dy = W / nyEff;

    const grid = Array.from({ length: nxEff }, () => Array(nyEff).fill(null));
    dist.forEach((p) => {
      const i = Math.min(nxEff - 1, Math.max(0, Math.floor((Number(p.x) + L / 2) / dx + 1e-9)));
      const j = Math.min(nyEff - 1, Math.max(0, Math.floor((Number(p.y) + W / 2) / dy + 1e-9)));
      grid[i][j] = p;
    });

    const pressures = dist.map((p) => Number(p.pressure_kPa) || 0).filter((v) => Number.isFinite(v));
    const minP = pressures.length ? Math.min(...pressures) : 0;
    const maxP = pressures.length ? Math.max(...pressures) : 0;

    const cells = [];
    for (let i = 0; i < nxEff; i += 1) {
      for (let j = 0; j < nyEff; j += 1) {
        const p = grid[i][j];
        const pk = p ? Number(p.pressure_kPa) || 0 : NaN;
        const sx = -L / 2 + i * dx;
        const sy = -W / 2 + j * dy;
        const px = offsetX + capW / 2 + sy * scale;
        const py = offsetY + capH / 2 + sx * scale;
        const cw = dy * scale;
        const ch = dx * scale;
        const fill = p ? colorForPressure(pk, minP, maxP) : '#f5f5f5';
        const label = p ? (Math.abs(pk) >= 100 ? pk.toFixed(0) : pk.toFixed(1)) : '—';
        const fs = nxEff >= 10 ? 6 : 7;
        cells.push(
          <g key={`c-${i}-${j}`}>
            <rect
              x={px}
              y={py}
              width={cw}
              height={ch}
              fill={fill}
              stroke="#455a64"
              strokeWidth={0.75}
            />
            <text
              x={px + cw / 2}
              y={py + ch / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={fs}
              fill="#263238"
              fontWeight="600"
            >
              {label}
            </text>
          </g>
        );
      }
    }

    const capOutline = (
      <rect
        x={offsetX}
        y={offsetY}
        width={capW}
        height={capH}
        fill="none"
        stroke="#37474f"
        strokeWidth={2}
      />
    );

    const svgInner = (
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        style={{
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          border: '1px solid #e0e0e0',
          background: '#fafafa',
        }}
      >
        <defs>
          <marker id={axisMarkerId} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#455a64" />
          </marker>
        </defs>
        <g transform={`translate(0, ${translateY})`}>
          <text x={svgW / 2} y={18} textAnchor="middle" fontSize="11" fill="#546e7a" fontWeight="bold">
            Cap underside — contact pressure (kPa), {nxEff}×{nyEff} soil elements
          </text>
          <text x={svgW / 2} y={32} textAnchor="middle" fontSize="9" fill="#78909c">
            Plan view (same orientation as foundation diagram: {W.toFixed(2)} m × {L.toFixed(2)} m)
          </text>
          <line
            x1={offsetX + capW / 2}
            y1={offsetY + capH / 2}
            x2={offsetX + capW / 2 + 30}
            y2={offsetY + capH / 2}
            stroke="#2e7d32"
            strokeWidth="1.8"
            markerEnd={`url(#${axisMarkerId})`}
          />
          <text x={offsetX + capW / 2 + 34} y={offsetY + capH / 2 + 4} fontSize="9" fill="#2e7d32" fontWeight="bold">
            +X
          </text>
          <line
            x1={offsetX + capW / 2}
            y1={offsetY + capH / 2}
            x2={offsetX + capW / 2}
            y2={offsetY + capH / 2 - 30}
            stroke="#6a1b9a"
            strokeWidth="1.8"
            markerEnd={`url(#${axisMarkerId})`}
          />
          <text x={offsetX + capW / 2 + 4} y={offsetY + capH / 2 - 34} fontSize="9" fill="#6a1b9a" fontWeight="bold">
            +Y
          </text>
          {capOutline}
          {cells}
        </g>
      </svg>
    );

    const legendText =
      maxP > minP
        ? `Range ${minP.toFixed(1)} – ${maxP.toFixed(1)} kPa (colour scale)`
        : `Uniform ${minP.toFixed(1)} kPa`;

    return { svg: svgInner, legend: legendText, nx: nxEff, ny: nyEff, minP, maxP };
  }, [foundation_geometry, dist, axisMarkerId]);

  if (allZero) return null;

  if (!foundation_geometry) {
    return (
      <Card>
        <CardHeader title={title || 'Cap element pressures (plan)'} subheader={subheader} />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Foundation geometry not available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!svg) {
    return (
      <Card>
        <CardHeader title={title || 'Cap element pressures (plan)'} subheader={subheader} />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {input_parameters?.foundation_type === 'spread_footing'
              ? 'Soil spring grid pressures are not available for this result.'
              : 'Slab bearing pressure distribution not available — run analysis again.'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={title || 'Cap element pressures (plan)'}
        subheader={subheader || `${nx}×${ny} Winkler elements on the cap underside`}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {legend}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Coordinate system: +X to the right, +Y upward (plan view)
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, width: '100%', maxWidth: '100%' }}>{svg}</Box>
      </CardContent>
    </Card>
  );
};

export default CapElementPressureMatrix;
